import ReactGA from 'react-ga4';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-ZYC7L6SDYM';

// Initialize Google Analytics
export const initGA = () => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.initialize(GA_MEASUREMENT_ID);
    console.log('Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
  } else {
    console.log('Google Analytics disabled in development mode');
  }
};

// Track page views
export const trackPageView = (path: string) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.send({ hitType: 'pageview', page: path });
    console.log('GA Page View:', path);
  }
};

// Track custom events
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    console.log('GA Event:', { category, action, label, value });
  }
};

// Track user engagement
export const trackUserEngagement = (engagementTimeMs: number) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.event({
      category: 'User Engagement',
      action: 'engagement_time_msec',
      value: engagementTimeMs,
    });
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent('Form', 'submit', `${formName}_${success ? 'success' : 'error'}`);
};

// Track file uploads
export const trackFileUpload = (fileType: string, fileSize: number) => {
  trackEvent('File Upload', 'upload', fileType, fileSize);
};

// Track review creation
export const trackReviewCreation = (reviewType: 'anonymous' | 'authenticated') => {
  trackEvent('Review', 'create', reviewType);
};

// Track review completion
export const trackReviewCompletion = (reviewType: 'anonymous' | 'authenticated') => {
  trackEvent('Review', 'complete', reviewType);
};

// Track review creation (legacy function for compatibility)
export const trackReviewCreated = (properties?: Record<string, any>) => {
  trackEvent('Review', 'created', 'authenticated', 1);
  if (process.env.NODE_ENV === 'production') {
    console.log('GA Review Created:', properties);
  }
};

// Track file upload (legacy function for compatibility)
export const trackFileUploaded = (fileType: string, fileSize: number, properties?: Record<string, any>) => {
  trackFileUpload(fileType, fileSize);
  if (process.env.NODE_ENV === 'production') {
    console.log('GA File Uploaded:', properties);
  }
};

// Track button clicks (legacy function for compatibility)
export const trackButtonClick = (buttonName: string, location: string, properties?: Record<string, any>) => {
  trackEvent('Button Click', buttonName, location);
  if (process.env.NODE_ENV === 'production') {
    console.log('GA Button Click:', properties);
  }
};

// Track authentication events
export const trackAuthEvent = (action: 'sign_in' | 'sign_up' | 'sign_out', method: string) => {
  trackEvent('Authentication', action, method);
};

// Track subscription events
export const trackSubscriptionEvent = (action: 'subscribe' | 'unsubscribe' | 'upgrade' | 'downgrade', plan?: string) => {
  trackEvent('Subscription', action, plan);
};

// Track error events
export const trackError = (errorType: string, errorMessage: string) => {
  trackEvent('Error', errorType, errorMessage);
};

// Track anonymous review conversion
export const trackAnonymousReviewConversion = () => {
  if (process.env.NODE_ENV === 'production') {
    // Use the global gtag function for Google Ads conversion tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-903738191/tgdRCNeStf8aEM_m964D',
        'value': 1.0,
        'currency': 'EUR'
      });
      console.log('GA Anonymous Review Conversion tracked');
    }
  } else {
    console.log('GA Anonymous Review Conversion (development mode)');
  }
};

// Track sign-up conversion
export const trackSignUpConversion = () => {
  if (process.env.NODE_ENV === 'production') {
    // Use the global gtag function for Google Ads conversion tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-903738191/B2JxCK-px4AbEM_m964D',
        'value': 10.0,
        'currency': 'EUR'
      });
      console.log('GA Sign-Up Conversion tracked');
    }
  } else {
    console.log('GA Sign-Up Conversion (development mode)');
  }
}; 